// library imports
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// store imports
import { useDomainStore, useFamilyStore, useHybridPedigreeStore, useUIStore } from '@/hooks/useStores';

// styles
import '@/components/pedigree/pedigree.css';
import './hybrid-pedigree-screen.css';

// utils – chart height derived from line options at current viewport
import { getLineOptionsForViewport, getMinVerticalLineSpacePx } from '@/utils/relationshipLines';
import { pushTreeRecentAnchor } from '@/utils/treeRecentAnchors';

// component imports
import GrandparentsGeneration from '@/components/pedigree/GrandparentsGeneration';
import ParentsGeneration from '@/components/pedigree/ParentsGeneration';
import RootGeneration from '@/components/pedigree/RootGeneration';
import ChildrenGeneration from '@/components/pedigree/ChildrenGeneration';
import RelationshipLines from '@/components/pedigree/RelationshipLines';
import TreeRecentAnchors from '@/components/pedigree/TreeRecentAnchors';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import HelpDot from '@/components/help/HelpDot';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  HYBRID_PEDIGREE_TOUR_STEPS,
  HYBRID_PEDIGREE_TOUR_STORAGE_KEY,
  resolveHybridPedigreeTourSteps
} from '@/data/hybrid-pedigree-tour';
import { TREE_LINE_STYLES_TIP } from '@/data/help-inline-tips';
import * as Switch from '@radix-ui/react-switch';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import Target from '@/icons/Target';
import Search from '@/icons/Search';
import Users from '@/icons/Users';
import { canModifyFamilyMembers } from '@/utils/rolePermissions';
import { getChildIdFromPlaceholderId, isParentOrGrandparentPlaceholder } from '@/utils/placeholderUtils';
import { petsApi } from '@/api/petsWrapper';
import type { PetResponse } from 'we-tree-api-client/v2';

const HybridPedigreeScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tPedigree } = useTranslation('pedigree');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const hybridPedigreeStore = useHybridPedigreeStore();
  const uiStore = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId: familyIdParam, familyMemberId: familyMemberIdParam } = useParams<{
    familyId: string;
    familyMemberId: string;
  }>();
  // Local state for UI
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [canAddByMemberId, setCanAddByMemberId] = useState<Record<string, boolean>>({});
  const [rootPets, setRootPets] = useState<PetResponse[]>([]);
  const [rootPetsLoading, setRootPetsLoading] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const showPetsInRoot = uiStore.showPetsInRoot;

  const familyDisplayName =
    domainStore.currentFamily?.displayName?.trim() ||
    domainStore.currentFamily?.name?.trim() ||
    t('tree.familyTreeFallback');

  // Clear root pets when toggle is off
  useEffect(() => {
    if (!showPetsInRoot) {
      setRootPets([]);
      setRootPetsLoading(false);
    }
  }, [showPetsInRoot]);

  // Layout CSS vars: required vertical space from line options + card height (so chart isn’t compressed)
  const [layoutVars, setLayoutVars] = useState(() => {
    const h = typeof window !== 'undefined' ? window.innerHeight : 0;
    const w = typeof window !== 'undefined' ? window.innerWidth : undefined;
    const opts = getLineOptionsForViewport(h, w);
    return {
      lineVerticalSpacePx: getMinVerticalLineSpacePx(opts),
      cardHeightPx: opts.cardHeight
    };
  });
  useEffect(() => {
    const update = () => {
      const opts = getLineOptionsForViewport(window.innerHeight, window.innerWidth);
      setLayoutVars({
        lineVerticalSpacePx: getMinVerticalLineSpacePx(opts),
        cardHeightPx: opts.cardHeight
      });
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    // Load family tree data using stores
    const loadFamilyTree = async () => {
      setErrorMessage(null);

      // Determine root family member from URL parameter
      if (!familyIdParam) {
        setErrorMessage(t('tree.errors.noFamily'));
        return;
      }
      if (!familyMemberIdParam) {
        setErrorMessage(t('tree.errors.noMemberId'));
        return;
      }

      try {
        // Set the selected family in the root store to enable family-context menus
        domainStore.setSelectedFamily(familyIdParam);

        // Clear tree visualization when switching families
        // This ensures placeholders and old tree data are removed before loading new family
        hybridPedigreeStore.clearTree();

        /**
         ***************************************************************
         * STEP 1: Load all family data into FamilyStore first
         * (Needed for 'user' case to find familyMemberId by personId)
         * Note: FamilyStore.loadFamilyData() clears people and relationships,
         * which will remove any placeholders from the previous family
         ***************************************************************
         */
        await familyStore.loadFamilyData(familyIdParam);

        // Verify that data was loaded successfully
        if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
          // eslint-disable-next-line no-console
          console.error('[HybridPedigreeScreen] Family data not loaded:', {
            isLoaded: familyStore.isLoaded,
            isLoading: familyStore.isLoading,
            familyId: familyStore.familyId,
            familyIdParam,
            lastLoadedAt: familyStore.lastLoadedAt
          });
          setErrorMessage(t('tree.errors.loadDataFailed'));
          return;
        }

        // Check if we have any family members
        if (familyStore.people.size === 0) {
          // eslint-disable-next-line no-console
          console.error('[HybridPedigreeScreen] No family members found after load:', {
            familyId: familyIdParam,
            isLoaded: familyStore.isLoaded,
            isLoading: familyStore.isLoading,
            familyIdMatches: familyStore.familyId === familyIdParam,
            peopleSize: familyStore.people.size,
            relationshipsSize: familyStore.relationships.size
          });
          setErrorMessage(t('tree.errors.noMembersAfterClaim'));
          return;
        }

        /**
         ***************************************************************
         * STEP 2: Resolve the family member ID on which to base the tree
         ***************************************************************
         */
        let resolvedFamilyMemberId: string | null = null;

        if (familyMemberIdParam === 'user') {
          resolvedFamilyMemberId = familyStore.getFamilyMemberIdForUser(
            domainStore.user?.personId,
            domainStore.user?.id
          );

          if (!resolvedFamilyMemberId) {
            // User is not found in this family, fallback to 'first'
            resolvedFamilyMemberId = familyStore.getRootPersonFamilyMemberId();
          }
        } else if (familyMemberIdParam === 'first') {
          // For 'first', get the oldest ancestor using store view
          resolvedFamilyMemberId = familyStore.getRootPersonFamilyMemberId();
        } else {
          // Direct family member ID provided
          resolvedFamilyMemberId = familyMemberIdParam;
        }

        // Validate that we have a valid family member ID
        if (!resolvedFamilyMemberId) {
          setErrorMessage(t('tree.errors.noMembers'));
          return;
        }

        // Verify that the resolved family member ID actually exists in the people map
        if (!familyStore.people.has(resolvedFamilyMemberId)) {
          // eslint-disable-next-line no-console
          console.error('[HybridPedigreeScreen] Resolved family member ID not found in people map:', {
            resolvedFamilyMemberId,
            familyMemberIdParam,
            peopleCount: familyStore.people.size,
            availableIds: Array.from(familyStore.people.keys())
          });
          setErrorMessage(t('tree.errors.memberNotFound'));
          return;
        }

        /**
         ***************************************************************
         * STEP 3: Build tree visualization from FamilyStore data
         ***************************************************************
         */
        hybridPedigreeStore.buildTree(resolvedFamilyMemberId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load family tree data:', error);
        setErrorMessage(t('tree.errors.loadTreeFailed'));
      }
    };

    loadFamilyTree();
  }, [domainStore, familyStore, hybridPedigreeStore, familyIdParam, familyMemberIdParam, t]);

  const handleAddClick = (parentFamilyMemberId: string, option: 'child' | 'spouse' | 'parent' | 'pet') => {
    if (familyIdParam) {
      const currentAnchorId = hybridPedigreeStore.treeAnchorFamilyMemberId;
      const navState = {
        state: {
          from: 'tree' as const,
          originalAnchorId: currentAnchorId,
          returnTo: `${location.pathname}${location.search}`
        }
      };
      if (option === 'parent') {
        navigate(`/families/${familyIdParam}/people/add?childId=${parentFamilyMemberId}&type=parent`, navState);
      } else if (option === 'pet') {
        navigate(`/families/${familyIdParam}/pets/add?parentId=${parentFamilyMemberId}`, navState);
      } else {
        navigate(`/families/${familyIdParam}/people/add?parentId=${parentFamilyMemberId}&type=${option}`, navState);
      }
    }
  };

  // Handle navigation to tree view (re-anchor tree on this person)
  const handleTreeViewClick = (clickedFamilyMemberId: string) => {
    if (!familyIdParam) return;
    const currentAnchorId = hybridPedigreeStore.treeAnchorFamilyMemberId;
    if (currentAnchorId && currentAnchorId !== clickedFamilyMemberId) {
      pushTreeRecentAnchor(familyIdParam, currentAnchorId, clickedFamilyMemberId);
    }
    navigate(`/families/${familyIdParam}/hybrid-pedigree/${clickedFamilyMemberId}`);
  };

  const handleAnchorOnMe = () => {
    if (!familyIdParam) return;
    const myFamilyMemberId = familyStore.getFamilyMemberIdForUser(domainStore.user?.personId, domainStore.user?.id);
    if (myFamilyMemberId) {
      handleTreeViewClick(myFamilyMemberId);
    }
  };

  const handleFindPerson = () => {
    if (familyIdParam) {
      navigate(`/families/${familyIdParam}/members`);
    }
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      id: familyIdParam,
      name: domainStore.currentFamily.name,
      displayName,
      alternativeName,
      memberCount: domainStore.currentFamily.memberCount,
      createdAt: domainStore.currentFamily.createdAt,
      updatedAt: domainStore.currentFamily.updatedAt,
      createdBy: domainStore.currentFamily.createdBy ?? null,
      createdByNickname: domainStore.currentFamily.createdByNickname ?? null,
      updatedBy: domainStore.currentFamily.updatedBy ?? null,
      updatedByNickname: domainStore.currentFamily.updatedByNickname ?? null,
      userRole: domainStore.currentFamily.userRole ?? null
    });
  };

  // Handle navigation to profile view (or Add Spouse for root placeholders, Add Parent for parent/grandparent placeholders)
  const handleProfileClick = (clickedFamilyMemberId: string) => {
    if (!familyIdParam) return;
    const currentAnchorId = hybridPedigreeStore.treeAnchorFamilyMemberId;

    if (clickedFamilyMemberId.startsWith('placeholder-')) {
      if (isParentOrGrandparentPlaceholder(clickedFamilyMemberId)) {
        const childId = getChildIdFromPlaceholderId(clickedFamilyMemberId, {
          treeAnchorFamilyMemberId: hybridPedigreeStore.treeAnchorFamilyMemberId,
          leftParentFamilyMemberId: hybridPedigreeStore.parentsGen.leftParentFamilyMemberId,
          rightParentFamilyMemberId: hybridPedigreeStore.parentsGen.rightParentFamilyMemberId
        });
        if (childId && !childId.startsWith('placeholder-')) {
          navigate(`/families/${familyIdParam}/people/add?childId=${childId}&type=parent`, {
            state: {
              from: 'tree',
              originalAnchorId: currentAnchorId,
              returnTo: `${location.pathname}${location.search}`
            }
          });
        }
      } else {
        const { leftRootFamilyMemberId, rightRootFamilyMemberId } = hybridPedigreeStore.rootGen;
        const partnerId =
          clickedFamilyMemberId === leftRootFamilyMemberId ? rightRootFamilyMemberId : leftRootFamilyMemberId;
        if (partnerId && !partnerId.startsWith('placeholder-')) {
          navigate(`/families/${familyIdParam}/people/add?parentId=${partnerId}&type=spouse`, {
            state: {
              from: 'tree',
              originalAnchorId: currentAnchorId,
              returnTo: `${location.pathname}${location.search}`
            }
          });
        }
      }
      return;
    }

    navigate(`/families/${familyIdParam}/people/${clickedFamilyMemberId}`, {
      state: { from: 'tree', originalAnchorId: currentAnchorId }
    });
  };

  // Get tree data from HybridPedigreeStore
  const hasTreeData = hybridPedigreeStore.isLoaded && hybridPedigreeStore.hasTreeAnchor;
  const treeTour = useScreenTour({
    steps: HYBRID_PEDIGREE_TOUR_STEPS,
    completedStorageKey: HYBRID_PEDIGREE_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveHybridPedigreeTourSteps(steps, hasTreeData)
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  const hybridStore = hybridPedigreeStore;
  const visibleIdsKey = hasTreeData
    ? [
        hybridStore.rootGen.leftRootFamilyMemberId,
        hybridStore.rootGen.rightRootFamilyMemberId,
        hybridStore.parentsGen.leftParentFamilyMemberId,
        hybridStore.parentsGen.rightParentFamilyMemberId,
        hybridStore.grandparentsGen.leftGrandparentsLeftFamilyMemberId,
        hybridStore.grandparentsGen.leftGrandparentsRightFamilyMemberId,
        hybridStore.grandparentsGen.rightGrandparentsLeftFamilyMemberId,
        hybridStore.grandparentsGen.rightGrandparentsRightFamilyMemberId,
        ...hybridStore.childrenGen.childrenFamilyMemberIds
      ]
        .filter(Boolean)
        .sort()
        .join(',')
    : '';

  useEffect(() => {
    if (!visibleIdsKey) return;
    const ids = visibleIdsKey.split(',').filter((id) => !id.startsWith('placeholder-'));
    if (ids.length === 0) return;
    let cancelled = false;
    const loadCanAdd = async () => {
      const results = await canModifyFamilyMembers(ids);
      if (!cancelled) {
        setCanAddByMemberId((prev) => ({ ...prev, ...results }));
      }
    };
    loadCanAdd();
    return () => {
      cancelled = true;
    };
  }, [visibleIdsKey]);

  // Load pets for root couple when showPetsInRoot is true
  const leftRootId = hybridPedigreeStore.rootGen.leftRootFamilyMemberId;
  const rightRootId = hybridPedigreeStore.rootGen.rightRootFamilyMemberId;
  const rootOwnerIds = [leftRootId, rightRootId].filter((id): id is string => !!id && !id.startsWith('placeholder-'));
  const rootOwnerIdsKey = rootOwnerIds.sort().join(',');

  useEffect(() => {
    if (!showPetsInRoot || !rootOwnerIdsKey || !familyIdParam) return;
    const ownerIds = rootOwnerIdsKey.split(',');
    let cancelled = false;
    setRootPetsLoading(true);
    const loadPets = async () => {
      const allPets: PetResponse[] = [];
      const seenIds = new Set<string>();
      for (const ownerId of ownerIds) {
        const result = await petsApi.getPetsByOwner(ownerId);
        if (cancelled) return;
        const pets = result.success ? (result.data?.pets ?? []) : [];
        for (const pet of pets) {
          if (pet?.familyMemberId && !seenIds.has(pet.familyMemberId)) {
            seenIds.add(pet.familyMemberId);
            allPets.push(pet);
          }
        }
      }
      if (!cancelled) {
        setRootPets(allPets);
        setRootPetsLoading(false);
      }
    };
    loadPets();
    return () => {
      cancelled = true;
      setRootPetsLoading(false);
    };
  }, [showPetsInRoot, rootOwnerIdsKey, familyIdParam]);

  const handlePetProfileClick = (petFamilyMemberId: string) => {
    if (familyIdParam) {
      const currentAnchorId = hybridPedigreeStore.treeAnchorFamilyMemberId;
      navigate(`/families/${familyIdParam}/pets/${petFamilyMemberId}`, {
        state: { from: 'tree', originalAnchorId: currentAnchorId }
      });
    }
  };

  if (errorMessage) {
    return (
      <div className="pedigree-screen__error">
        <h3 className="pedigree-screen__error-title">{errorMessage}</h3>
        <p className="pedigree-screen__error-hint">
          <Trans
            i18nKey="tree.errorRefreshHint"
            ns="screens"
            components={{
              refreshLink: (
                <a
                  href="#"
                  className="pedigree-screen__refresh-link"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.reload();
                  }}
                />
              )
            }}
          />
        </p>
      </div>
    );
  }

  const memberCount = domainStore.currentFamily?.memberCount ?? familyStore.people.size;
  const treeAnchorId = hybridPedigreeStore.treeAnchorFamilyMemberId;

  return (
    <div className="tree-page pedigree-tree-scope">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('tree.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={treeTour.startTour}
        intro={
          <>
            {t('tree.lede')} <ScreenHelpDot />
          </>
        }
        introTrailing={
          <div className="tree-page__intro-actions" data-tour="tree-header-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleAnchorOnMe}
              title={t('tree.anchorOnYouTitle')}
            >
              <Target width={14} height={14} />
              {tForms('actions.anchorOnMe')}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleFindPerson}
              title={tForms('actions.findAPerson')}
            >
              <Search width={14} height={14} />
              {tForms('actions.findPerson')}
            </button>
          </div>
        }
      />

      {hasTreeData && (
        <div className="tree-meta-row" data-tour="tree-meta">
          <div className="tree-meta-row__left">
            <span className="tree-meta__chip">
              <Users width={13} height={13} />
              <strong>{memberCount}</strong> {tPedigree('membersLabel')}
            </span>
            <TreeRecentAnchors familyId={familyIdParam!} currentAnchorId={treeAnchorId} onPick={handleTreeViewClick} />
          </div>
          <div className="tree-meta-row__right">
            <div className="tree-legend">
              <span className="tree-legend__heading">
                {tPedigree('lineStyles')}
                <HelpDot
                  title={TREE_LINE_STYLES_TIP.title}
                  body={TREE_LINE_STYLES_TIP.body}
                  storageKey={TREE_LINE_STYLES_TIP.storageKey}
                  docId="tree-line-styles"
                />
              </span>
              <span className="tree-legend__item">
                <span className="tree-legend__swatch" />
                {tPedigree('biological')}
              </span>
              <span className="tree-legend__item">
                <span className="tree-legend__swatch tree-legend__swatch--dashed" />
                {tPedigree('stepAdopted')}
              </span>
            </div>
            <label className="tree-show-pets">
              <Switch.Root
                className="tree-show-pets__switch"
                checked={showPetsInRoot}
                onCheckedChange={(checked) => uiStore.setShowPetsInRoot(checked)}
                aria-label={tPedigree('showPetsHint')}
              >
                <Switch.Thumb className="tree-show-pets__switch-thumb" />
              </Switch.Root>
              {tPedigree('showPets')}
            </label>
          </div>
        </div>
      )}

      <main className="tree-page__main">
        {hasTreeData ? (
          <div className="tree-stage" data-tour="tree-stage">
            <div className="tree-stage__scroll">
              <div
                className="pedigree-container"
                ref={containerRef}
                style={
                  {
                    '--pedigree-line-vertical-space-px': layoutVars.lineVerticalSpacePx,
                    '--pedigree-card-height-px': layoutVars.cardHeightPx
                  } as React.CSSProperties
                }
              >
                <ErrorBoundary>
                  <RelationshipLines treeData={hybridPedigreeStore} containerRef={containerRef} />
                </ErrorBoundary>
                <ErrorBoundary>
                  <GrandparentsGeneration
                    onTreeViewClick={handleTreeViewClick}
                    onProfileClick={handleProfileClick}
                    onAddClick={handleAddClick}
                    canAddByMemberId={canAddByMemberId}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <ParentsGeneration
                    onTreeViewClick={handleTreeViewClick}
                    onProfileClick={handleProfileClick}
                    onAddClick={handleAddClick}
                    canAddByMemberId={canAddByMemberId}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <RootGeneration
                    onTreeViewClick={handleTreeViewClick}
                    onProfileClick={handleProfileClick}
                    onAddClick={handleAddClick}
                    canAddByMemberId={canAddByMemberId}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <ChildrenGeneration
                    familyId={familyIdParam ?? undefined}
                    onTreeViewClick={handleTreeViewClick}
                    onProfileClick={handleProfileClick}
                    onAddClick={handleAddClick}
                    canAddByMemberId={canAddByMemberId}
                    showPets={showPetsInRoot}
                    rootPets={rootPets}
                    rootPetsLoading={rootPetsLoading}
                    onPetProfileClick={handlePetProfileClick}
                  />
                </ErrorBoundary>
              </div>
            </div>
          </div>
        ) : (
          <div className="pedigree-screen__loading">
            <div className="pedigree-screen__loading-content">
              <div className="pedigree-screen__spinner" />
              <p className="pedigree-screen__loading-text">{t('tree.loading')}</p>
            </div>
          </div>
        )}
      </main>

      {showEditNameModal && familyIdParam && domainStore.currentFamily && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={domainStore.currentFamily.displayName || domainStore.currentFamily.name}
          canonicalName={domainStore.currentFamily.name}
          hasAlternative={!!domainStore.currentFamily.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      <ScreenTourLayer tour={treeTour} />
    </div>
  );
});

export default HybridPedigreeScreen;
